import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

/**
 * useAutoRefetch — no-manual-refresh helper for data pages.
 *
 * Triggers `refetch()` when ANY of these fire:
 *   1. Tab regains focus                        (window.focus)
 *   2. Tab becomes visible after being hidden   (visibilitychange)
 *   3. Server reports a mutation in a watched collection since our last check
 *      (30-second poll of /api/mutations/since)
 *
 * Deliberately does NOT trigger on interval polling for the same page — that
 * would be wasteful. The mutation poll only fires the callback when there's
 * an actual change, so quiet pages stay quiet.
 *
 * Usage:
 *   useAutoRefetch(loadWorkOrders, { collections: ['work_orders', 'master_projects'] });
 *
 * Optional `enabled` flag lets pages disable the refetcher when they're not
 * mounted / visible.
 *
 * ---- Sync-health tracking ---------------------------------------------
 * Every mounted useAutoRefetch tracks its consecutive poll failures in a
 * module-level singleton (`syncHealth`). The Sidebar reads that singleton
 * via `useSyncHealth()` and shows a red dot when *any* page's polling loop
 * has failed 3+ times in a row. On the next successful poll, the counter
 * resets and the dot disappears.
 */

// ------- Sync health singleton --------------------------------------------
const HEALTH_FAIL_THRESHOLD = 3;

const syncHealth = {
  consecutive: 0,
  listeners: new Set(),
  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); },
  _notify() { for (const fn of this.listeners) { try { fn(this.consecutive); } catch { /* noop */ } } },
  recordSuccess() {
    if (this.consecutive !== 0) {
      this.consecutive = 0;
      this._notify();
    }
  },
  recordFailure() {
    this.consecutive += 1;
    this._notify();
  },
};

/** React hook — returns `true` when the mutation poll has failed
 *  `HEALTH_FAIL_THRESHOLD` (3) or more consecutive times. Consumers should
 *  render a small badge indicator on that signal. */
export function useSyncHealth() {
  const [count, setCount] = useState(syncHealth.consecutive);
  useEffect(() => syncHealth.subscribe(setCount), []);
  return {
    unhealthy: count >= HEALTH_FAIL_THRESHOLD,
    consecutiveFailures: count,
  };
}

// ------- The refetcher hook -----------------------------------------------
export function useAutoRefetch(refetch, { collections = [], enabled = true, pollMs = 30000 } = {}) {
  const cbRef = useRef(refetch);
  const lastTsRef = useRef(null);
  cbRef.current = refetch;

  const call = useCallback(() => {
    if (!enabled) return;
    try { cbRef.current?.(); } catch { /* soft-fail */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => call();
    const onVisible = () => { if (document.visibilityState === 'visible') call(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [call, enabled]);

  useEffect(() => {
    if (!enabled || !collections.length) return;
    let disposed = false;
    let handle = null;

    const tick = async () => {
      if (disposed) return;
      try {
        const params = new URLSearchParams();
        params.set('collections', collections.join(','));
        if (lastTsRef.current) params.set('since', lastTsRef.current);
        const r = await api.get(`/mutations/since?${params.toString()}`);
        if (disposed) return;
        syncHealth.recordSuccess();
        if (r.data.count > 0) {
          lastTsRef.current = r.data.latest_ts;
          call();
        } else if (r.data.latest_ts && !lastTsRef.current) {
          // First tick — remember the current head so we don't refetch on the
          // next tick with no changes.
          lastTsRef.current = r.data.latest_ts;
        }
      } catch {
        // Any error (network, 5xx, timeout) counts as a health hit. Once we
        // cross the threshold the Sidebar surfaces the red dot.
        if (!disposed) syncHealth.recordFailure();
      }
      handle = setTimeout(tick, pollMs);
    };
    // Small initial delay to let the page finish its first paint before we
    // fire a background request.
    handle = setTimeout(tick, 2500);
    return () => {
      disposed = true;
      if (handle) clearTimeout(handle);
    };
  }, [enabled, collections.join(','), pollMs, call]); // eslint-disable-line react-hooks/exhaustive-deps
}
